import {
  createContext,
  type DragEvent as ReactDragEvent,
  type FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Project, ProjectBoardGroup, SessionUser, Workspace } from '@katsquare/types';
import { Spinner } from './ui';
import {
  createProject,
  createWorkspace,
  deleteProject,
  deleteWorkspace,
  getProjectBoard,
  listProjects,
  listWorkspaces,
  renameProject,
  renameWorkspace,
  reorderProjects,
  reorderWorkspaces,
  restoreItem,
} from '../lib/api';
import { useLogout, useSession } from '../features/auth/useSession';
import { useToast } from './Toast';
import { usePlanLimit } from './PlanLimitDialog';
import { WelcomeModal } from './WelcomeModal';
import { IconCheck, IconComment, IconSettings, IconTag, IconTrash, IconUser } from './icons';

/**
 * A broadcast "expand all / collapse all" command for the tree. Each magazine
 * and month node re-applies it via an effect keyed on the bumping nonce.
 */
type TreeCommand = { expand: boolean; nonce: number } | null;
const TreeCommandContext = createContext<TreeCommand>(null);

interface SortableRowProps {
  draggable: boolean;
  onDragStart: (e: ReactDragEvent) => void;
  onDragOver: (e: ReactDragEvent) => void;
  onDrop: (e: ReactDragEvent) => void;
  onDragEnd: () => void;
}
interface SortableHandleProps {
  onPointerDown: () => void;
}

/**
 * Minimal native (dependency-free) drag-and-drop reordering for a vertical list
 * of `{id}` rows. A row is only draggable once grabbed by its handle (so name
 * links and ⋮ menus still click normally); on drop, the new top-to-bottom order
 * is handed back via `onReorder`.
 */
function useSortable(orderedIds: string[], onReorder: (ids: string[]) => void) {
  const [armedId, setArmedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const reset = () => {
    setArmedId(null);
    setDragId(null);
    setOverId(null);
  };
  const rowProps = (id: string): SortableRowProps => ({
    draggable: armedId === id,
    onDragStart: (e) => {
      setDragId(id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id); // Firefox needs data set
    },
    onDragOver: (e) => {
      if (!dragId || dragId === id) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overId !== id) setOverId(id);
    },
    onDrop: (e) => {
      if (!dragId || dragId === id) return;
      e.preventDefault();
      e.stopPropagation();
      const next = [...orderedIds];
      const from = next.indexOf(dragId);
      const to = next.indexOf(id);
      if (from !== -1 && to !== -1) {
        next.splice(from, 1);
        next.splice(to, 0, dragId);
        onReorder(next);
      }
      reset();
    },
    onDragEnd: reset,
  });
  const handleProps = (id: string): SortableHandleProps => ({ onPointerDown: () => setArmedId(id) });
  return { rowProps, handleProps, overId };
}

/** Grip icon that arms a row for dragging — spread `handle` (handleProps) onto it. */
function DragHandle({ handle, className = '' }: { handle: SortableHandleProps; className?: string }) {
  return (
    <span
      {...handle}
      role="button"
      aria-label="Drag to reorder"
      title="Drag to reorder"
      className={`flex h-5 w-5 shrink-0 cursor-grab items-center justify-center text-neutral-300 hover:text-neutral-500 active:cursor-grabbing ${className}`}
    >
      <svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" aria-hidden="true">
        <circle cx="6" cy="4" r="1.1" />
        <circle cx="10" cy="4" r="1.1" />
        <circle cx="6" cy="8" r="1.1" />
        <circle cx="10" cy="8" r="1.1" />
        <circle cx="6" cy="12" r="1.1" />
        <circle cx="10" cy="12" r="1.1" />
      </svg>
    </span>
  );
}

/** Single collapsible left panel: branding, workspaces/magazines, profile. */
export function AppShell() {
  const { user } = useSession();
  const [treeCmd, setTreeCmd] = useState<TreeCommand>(null);
  const broadcastTree = (expand: boolean) =>
    setTreeCmd((c) => ({ expand, nonce: (c?.nonce ?? 0) + 1 }));
  const logout = useLogout();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('ksq-sidebar-collapsed') === '1',
  );
  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('ksq-sidebar-collapsed', next ? '1' : '0');
      return next;
    });

  // On phones the sidebar is an off-canvas drawer; it closes on navigation.
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname, location.search]);
  const isDesktop = useIsDesktop();
  // The narrow "collapsed rail" is a desktop-only affordance — on mobile the
  // drawer always shows the full sidebar rather than the useless rail.
  const showCollapsed = collapsed && isDesktop;

  const workspaces = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });

  // Multiple workspaces are Business-only: allow the first, then only if the
  // account is on Business (mirrors the server-side check).
  const ownsAny = (workspaces.data ?? []).some((w) => w.role === 'OWNER');
  const canAddWorkspace = !ownsAny || user?.plan === 'BUSINESS';

  // Drag-to-reorder the workspace list (per-user order). Anyone may reorder
  // their own sidebar; the change is optimistic + persisted.
  const queryClient = useQueryClient();
  const toast = useToast();
  const reorderWs = useMutation({
    mutationFn: reorderWorkspaces,
    onError: (e: Error) => {
      toast(e.message);
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
  const wsSort = useSortable(
    (workspaces.data ?? []).map((w) => w.id),
    (ids) => {
      queryClient.setQueryData<Workspace[]>(['workspaces'], (old) =>
        old ? (ids.map((id) => old.find((w) => w.id === id)).filter(Boolean) as Workspace[]) : old,
      );
      reorderWs.mutate(ids);
    },
  );

  // `h-screen` (100vh) is taller than what a mobile browser actually shows — it
  // ignores the URL bar — so the bottom of the shell, and anything pinned there
  // (the PDF tool bar), sat off-screen with every ancestor `overflow-hidden`, so
  // there was no way to scroll to it. `dvh` tracks the visible viewport; the
  // plain 100vh stays as the fallback for browsers without it.
  return (
    <div className="flex h-screen bg-neutral-50 text-neutral-900 supports-[height:100dvh]:h-[100dvh]">
      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-neutral-200 bg-white transition-transform md:static md:z-auto md:translate-x-0 md:shrink-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${showCollapsed ? 'md:w-14' : 'md:w-64'}`}
      >
        {/* Branding + collapse toggle. Collapsed: toggle on top, brand runs
            vertically beneath it (in the original colour). */}
        <div
          className={`flex border-b border-neutral-200 py-3 ${
            showCollapsed ? 'flex-col items-center gap-3 px-2' : 'items-center gap-2 px-3'
          }`}
        >
          {showCollapsed ? (
            <>
              <button
                type="button"
                onClick={toggle}
                title="Expand sidebar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              >
                »
              </button>
              <Link
                to="/app"
                title="KatSquare Draft"
                className="text-sm font-bold tracking-wide text-neutral-900 [writing-mode:vertical-rl] rotate-180"
              >
                KatSquare Draft
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/app"
                title="KatSquare Draft"
                className="mr-auto min-w-0 truncate text-sm font-bold tracking-tight text-neutral-900"
              >
                KatSquare Draft
              </Link>
              {/* Desktop collapse toggle */}
              <button
                type="button"
                onClick={toggle}
                title="Collapse sidebar"
                className="hidden h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 md:flex"
              >
                «
              </button>
              {/* Mobile drawer close */}
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                title="Close menu"
                aria-label="Close menu"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 md:hidden"
              >
                ✕
              </button>
            </>
          )}
        </div>

        {/* Workspaces (hidden while collapsed) */}
        {showCollapsed ? (
          <div className="flex-1" />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
            {workspaces.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : workspaces.data?.length === 0 ? (
              <NewWorkspace />
            ) : (
              <TreeCommandContext.Provider value={treeCmd}>
                <div className="mb-2 flex items-center gap-3 px-2 text-[11px] font-medium text-neutral-400">
                  <button type="button" onClick={() => broadcastTree(true)} className="hover:text-neutral-700">
                    Expand all
                  </button>
                  <span className="text-neutral-200">·</span>
                  <button type="button" onClick={() => broadcastTree(false)} className="hover:text-neutral-700">
                    Collapse all
                  </button>
                </div>
                {workspaces.data?.map((w) => (
                  <WorkspaceSection
                    key={w.id}
                    workspace={w}
                    rowProps={wsSort.rowProps(w.id)}
                    handleProps={wsSort.handleProps(w.id)}
                    isOver={wsSort.overId === w.id}
                  />
                ))}
                <NewWorkspace compact canAdd={canAddWorkspace} />
              </TreeCommandContext.Provider>
            )}
          </div>
        )}

        <ProfileMenu
          user={user}
          logout={logout}
          collapsed={showCollapsed}
          teamWorkspaceId={
            (workspaces.data?.find((w) => w.role === 'OWNER') ?? workspaces.data?.[0])?.id ?? null
          }
        />
      </aside>

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar: hamburger + wordmark (the sidebar is a drawer here). */}
        <div className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-2.5 md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            title="Open menu"
            aria-label="Open menu"
            className="flex h-8 w-8 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/app" className="text-sm font-bold tracking-tight text-neutral-900">
            KatSquare Draft
          </Link>
        </div>
        <GraceBanner graceEndsAt={user?.graceEndsAt ?? null} />
        <div className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      <WelcomeModal />
    </div>
  );
}

/**
 * Shown only while a lapsed account is inside its post-lapse grace window, i.e.
 * its files are on a deletion clock. Deliberately loud and not dismissible —
 * the outcome is permanent.
 */
function GraceBanner({ graceEndsAt }: { graceEndsAt: string | null }) {
  if (!graceEndsAt) return null;
  const deadline = new Date(graceEndsAt);
  const daysLeft = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
  const when = deadline.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
      <span className="font-semibold">
        {daysLeft === 0
          ? 'Your PDFs are deleted today.'
          : `${daysLeft} day${daysLeft === 1 ? '' : 's'} until your PDFs are deleted.`}
      </span>
      <span className="text-red-700">
        Your plan ended. Every PDF in the workspaces you own is permanently deleted on {when} unless
        you renew.
      </span>
      <Link
        to="/pricing"
        className="ml-auto rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-800"
      >
        Renew plan
      </Link>
    </div>
  );
}

/** True on md+ viewports; drives the desktop-only collapsed rail. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function ProfileMenu({
  user,
  logout,
  collapsed,
  teamWorkspaceId,
}: {
  user: SessionUser | null;
  logout: () => Promise<void> | void;
  collapsed: boolean;
  teamWorkspaceId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const initials = (user?.name ?? '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="relative border-t border-neutral-200 p-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={user?.name ?? 'Account'}
        className={`flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 hover:bg-neutral-100 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
            {initials}
          </span>
        )}
        {!collapsed && (
          <span className="min-w-0 flex-1 truncate text-left text-sm text-neutral-700">{user?.name}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full left-2 z-20 mb-1 w-48 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
            <div className="border-b border-neutral-100 px-3 py-1.5">
              <p className="truncate text-xs font-medium text-neutral-900">{user?.name}</p>
              <p className="truncate text-[11px] text-neutral-400">{user?.email}</p>
            </div>
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100"
            >
              <IconUser className="h-4 w-4 text-neutral-400" /> Edit profile
            </Link>
            {teamWorkspaceId && (
              <Link
                to={`/workspaces/${teamWorkspaceId}/settings`}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100"
              >
                <IconSettings className="h-4 w-4 text-neutral-400" /> Team &amp; members
              </Link>
            )}
            <Link
              to="/pricing"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100"
            >
              <IconTag className="h-4 w-4 text-neutral-400" /> Plans &amp; pricing
            </Link>
            <Link
              to="/recycle-bin"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100"
            >
              <IconTrash className="h-4 w-4 text-neutral-400" /> Recycle bin
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
              className="block w-full border-t border-neutral-100 px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Keep the tree's open-comment badges live. They describe proofs nobody has
 * open, so no version-scoped stream ever reaches them and the board query has
 * no poll of its own — a workspace-wide stream is the only signal that does.
 */
function useWorkspaceEvents(workspaceId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const es = new EventSource(`/api/workspaces/${workspaceId}/events`, { withCredentials: true });
    let pending: ReturnType<typeof setTimeout> | undefined;
    // A burst (someone resolving five threads in a row) should cost one refetch.
    const refresh = () => {
      if (pending) return;
      pending = setTimeout(() => {
        pending = undefined;
        queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
        // The home page's attention list and activity feed read the same changes.
        queryClient.invalidateQueries({ queryKey: ['overview'] });
      }, 400);
    };
    es.addEventListener('review', refresh);
    // A dropped connection (sleep, flaky wifi) silently loses events, so treat
    // every reconnect as "you missed something" — the first open is the mount.
    let connected = false;
    es.addEventListener('open', () => {
      if (connected) refresh();
      connected = true;
    });
    return () => {
      if (pending) clearTimeout(pending);
      es.close();
    };
  }, [workspaceId, queryClient]);
}

function WorkspaceSection({
  workspace,
  rowProps,
  handleProps,
  isOver,
}: {
  workspace: Workspace;
  rowProps: SortableRowProps;
  handleProps: SortableHandleProps;
  isOver: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [menu, setMenu] = useState(false);

  const projects = useQuery({
    queryKey: ['projects', workspace.id],
    queryFn: () => listProjects(workspace.id),
  });
  useWorkspaceEvents(workspace.id);
  const addProject = useMutation({
    mutationFn: (name: string) => createProject(workspace.id, { name }),
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['projects', workspace.id] });
      setAdding(false);
      navigate(`/projects/${p.id}`);
    },
    onError: (e: Error) => toast(e.message),
  });
  const toast = useToast();
  const rename = useMutation({
    mutationFn: (name: string) => renameWorkspace(workspace.id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setRenaming(false);
    },
    onError: (e: Error) => toast(e.message),
  });
  const del = useMutation({
    mutationFn: () => deleteWorkspace(workspace.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/app');
      toast(`Workspace “${workspace.name}” moved to recycle bin`, {
        label: 'Undo',
        onClick: () =>
          restoreItem('workspace', workspace.id).then(() =>
            queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
          ),
      });
    },
    onError: (e: Error) => toast(e.message),
  });

  const empty = projects.data?.length === 0;
  const canEdit = workspace.role !== 'REVIEWER';

  // Drag-to-reorder magazines within this workspace (editors only).
  const reorderProj = useMutation({
    mutationFn: (ids: string[]) => reorderProjects(workspace.id, ids),
    onError: (e: Error) => {
      toast(e.message);
      queryClient.invalidateQueries({ queryKey: ['projects', workspace.id] });
    },
  });
  const projSort = useSortable(
    (projects.data ?? []).map((p) => p.id),
    (ids) => {
      queryClient.setQueryData<Project[]>(['projects', workspace.id], (old) =>
        old ? (ids.map((id) => old.find((p) => p.id === id)).filter(Boolean) as Project[]) : old,
      );
      reorderProj.mutate(ids);
    },
  );

  return (
    <div
      {...rowProps}
      className={`mb-4 rounded-md border-t-2 ${isOver ? 'border-blue-400' : 'border-transparent'}`}
    >
      {renaming ? (
        <InlineCreate
          initial={workspace.name}
          placeholder="Workspace name"
          onCreate={(n) => rename.mutate(n)}
          busy={rename.isPending}
          onCancel={() => setRenaming(false)}
          saveOnBlur
        />
      ) : confirming ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <span className="mr-auto truncate">Delete workspace?</span>
          <button type="button" onClick={() => del.mutate()} disabled={del.isPending} className="font-semibold hover:underline">
            {del.isPending ? '…' : 'Delete'}
          </button>
          <button type="button" onClick={() => setConfirming(false)} className="text-neutral-400 hover:text-neutral-700">
            Cancel
          </button>
        </div>
      ) : (
        <div className="group flex items-center gap-0.5 px-2 py-1">
          <DragHandle
            handle={handleProps}
            className="opacity-100 md:opacity-0 md:group-hover:opacity-100"
          />
          <span className="mr-auto truncate text-[15px] font-semibold text-neutral-800">
            {workspace.name}
          </span>
          <Link
            to={`/workspaces/${workspace.id}/settings`}
            title="Members & settings"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-neutral-400 opacity-100 hover:bg-neutral-100 hover:text-neutral-700 md:opacity-0 md:group-hover:opacity-100"
          >
            <IconSettings className="h-3.5 w-3.5" />
          </Link>
          {canEdit && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              title="New magazine"
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            >
              +
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenu((m) => !m)}
              title="Workspace options"
              aria-expanded={menu}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-neutral-400 opacity-100 hover:bg-neutral-100 hover:text-neutral-700 md:opacity-0 md:group-hover:opacity-100 aria-expanded:opacity-100"
            >
              ⋮
            </button>
            {menu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                <div className="absolute right-0 top-6 z-20 w-36 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                  <Link
                    to={`/workspaces/${workspace.id}/settings`}
                    onClick={() => setMenu(false)}
                    className="block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                  >
                    Settings & members
                  </Link>
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setMenu(false);
                          setRenaming(true);
                        }}
                        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMenu(false);
                          setConfirming(true);
                        }}
                        className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ul className="space-y-0.5">
        {projects.data?.map((p) => (
          <ProjectItem
            key={p.id}
            project={p}
            workspaceId={workspace.id}
            canEdit={canEdit}
            rowProps={projSort.rowProps(p.id)}
            handleProps={projSort.handleProps(p.id)}
            isOver={projSort.overId === p.id}
          />
        ))}
      </ul>
      {adding ? (
        <InlineCreate
          placeholder="Magazine name"
          onCreate={(n) => addProject.mutate(n)}
          busy={addProject.isPending}
          onCancel={() => setAdding(false)}
        />
      ) : empty && canEdit && !renaming && !confirming ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-0.5 w-full rounded-md px-2 py-1.5 text-left text-sm text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600"
        >
          + Add magazine
        </button>
      ) : null}
    </div>
  );
}

function ProjectItem({
  project,
  workspaceId,
  canEdit,
  rowProps,
  handleProps,
  isOver,
}: {
  project: { id: string; name: string };
  workspaceId: string;
  canEdit: boolean;
  rowProps: SortableRowProps;
  handleProps: SortableHandleProps;
  isOver: boolean;
}) {
  const { projectId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [menu, setMenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const treeCmd = useContext(TreeCommandContext);
  useEffect(() => {
    if (treeCmd) setExpanded(treeCmd.expand);
  }, [treeCmd]);
  // Highlight the magazine only when viewing it directly — not when a month
  // inside it is the selected one (?issue=), so a month click greys the month alone.
  const active = project.id === projectId && !sp.get('issue');
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projects', workspaceId] });
  const board = useQuery({
    queryKey: ['projectBoard', project.id],
    queryFn: () => getProjectBoard(project.id),
    enabled: expanded,
  });

  const toast = useToast();
  const rename = useMutation({
    mutationFn: (name: string) => renameProject(project.id, name),
    onSuccess: () => {
      invalidate();
      setRenaming(false);
    },
    onError: (e: Error) => toast(e.message),
  });
  const del = useMutation({
    mutationFn: () => deleteProject(project.id),
    onSuccess: () => {
      invalidate();
      if (active) navigate('/app');
      toast(`Magazine “${project.name}” moved to recycle bin`, {
        label: 'Undo',
        onClick: () => restoreItem('magazine', project.id).then(invalidate),
      });
    },
    onError: (e: Error) => toast(e.message),
  });

  if (renaming) {
    return (
      <li>
        <InlineCreate
          initial={project.name}
          placeholder="Magazine name"
          onCreate={(n) => rename.mutate(n)}
          busy={rename.isPending}
          onCancel={() => setRenaming(false)}
          saveOnBlur
        />
      </li>
    );
  }
  if (confirming) {
    return (
      <li className="flex items-center gap-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
        <span className="mr-auto truncate">Delete magazine?</span>
        <button type="button" onClick={() => del.mutate()} disabled={del.isPending} className="font-semibold hover:underline">
          {del.isPending ? '…' : 'Delete'}
        </button>
        <button type="button" onClick={() => setConfirming(false)} className="text-neutral-400 hover:text-neutral-700">
          Cancel
        </button>
      </li>
    );
  }

  return (
    <li
      {...rowProps}
      className={isOver ? 'rounded-md border-t-2 border-blue-400' : 'border-t-2 border-transparent'}
    >
      <div className="group flex items-center gap-0.5">
        {canEdit && (
          <DragHandle
            handle={handleProps}
            className="opacity-100 md:opacity-0 md:group-hover:opacity-100"
          />
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          title={expanded ? 'Collapse' : 'Expand'}
          className="flex h-6 w-6 shrink-0 items-center justify-center text-sm text-neutral-500 hover:text-neutral-800"
        >
          {expanded ? '▾' : '▸'}
        </button>
        <Link
          to={`/projects/${project.id}`}
          onClick={() => setExpanded(true)}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
            active ? 'bg-neutral-100 font-semibold text-neutral-900' : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          <BookIcon />
          <span className="truncate">{project.name}</span>
        </Link>
        {canEdit && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenu((m) => !m)}
            title="Magazine options"
            aria-expanded={menu}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 opacity-100 hover:bg-neutral-200 hover:text-neutral-700 md:opacity-0 md:group-hover:opacity-100 aria-expanded:opacity-100"
          >
            ⋮
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-6 z-20 w-32 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMenu(false);
                    setRenaming(true);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenu(false);
                    setConfirming(true);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {expanded && (
        <div className="ml-4 mt-0.5 border-l border-neutral-100 pl-2">
          {board.isPending ? (
            <div className="py-1 pl-3 text-xs text-neutral-400">Loading…</div>
          ) : board.data && board.data.length > 0 ? (
            <ul className="space-y-0.5">
              {board.data.map((g) => (
                <IssueNode key={g.issue.id} group={g} projectId={project.id} />
              ))}
            </ul>
          ) : (
            <div className="py-1 pl-3 text-xs text-neutral-400">No months yet.</div>
          )}
        </div>
      )}
    </li>
  );
}

function IssueNode({ group, projectId }: { group: ProjectBoardGroup; projectId: string }) {
  const [open, setOpen] = useState(true);
  const [sp] = useSearchParams();
  const treeCmd = useContext(TreeCommandContext);
  useEffect(() => {
    if (treeCmd) setOpen(treeCmd.expand);
  }, [treeCmd]);
  // Highlighted only when this month is the one clicked (?issue= in the URL), so
  // opening a PDF greys the PDF alone — not its parent month.
  const active = sp.get('issue') === group.issue.id;
  return (
    <li>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={open ? 'Collapse month' : 'Expand month'}
          aria-label={open ? 'Collapse month' : 'Expand month'}
          className="flex h-6 w-6 shrink-0 items-center justify-center text-sm text-neutral-500 hover:text-neutral-800"
        >
          {open ? '▾' : '▸'}
        </button>
        {/* Count lives INSIDE the link so the active pill highlights it too. */}
        <Link
          to={`/projects/${projectId}?issue=${group.issue.id}`}
          onClick={() => setOpen(true)}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-[13px] ${
            active
              ? 'bg-neutral-100 font-semibold text-neutral-900'
              : 'font-medium text-neutral-500 hover:bg-neutral-50'
          }`}
        >
          <span className="min-w-0 flex-1 truncate">{group.issue.name}</span>
          <span
            className={`shrink-0 text-[10px] ${
              active ? 'font-semibold text-neutral-700' : 'text-neutral-300'
            }`}
          >
            {group.rows.length}
          </span>
        </Link>
      </div>
      {open && group.rows.length > 0 && (
        <ul className="ml-4 space-y-0.5 border-l border-neutral-100 pl-2">
          {group.rows.map((r) => (
            <ProofNode key={r.proofId} proofId={r.proofId} name={r.proofName} openComments={r.openComments} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ProofNode({
  proofId,
  name,
  openComments,
}: {
  proofId: string;
  name: string;
  openComments: number;
}) {
  const params = useParams();
  const active = proofId === params.proofId;
  return (
    <li>
      <Link
        to={`/pdfs/${proofId}`}
        className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs ${
          active ? 'bg-neutral-100 font-semibold text-neutral-900' : 'text-neutral-500 hover:bg-neutral-50'
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{name}</span>
        {openComments > 0 && (
          <span className="flex shrink-0 items-center gap-0.5 text-neutral-400" title="Open comments">
            <IconComment className="h-3 w-3" />
            {openComments}
          </span>
        )}
      </Link>
    </li>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="h-4 w-4 shrink-0" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5a2 2 0 0 1 2-2h9v18H6a2 2 0 0 1-2-2z" />
      <path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" />
    </svg>
  );
}

function NewWorkspace({ compact, canAdd = true }: { compact?: boolean; canAdd?: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showPlanLimit = usePlanLimit();
  const [adding, setAdding] = useState(!compact);
  const add = useMutation({
    mutationFn: (name: string) => createWorkspace({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      if (compact) setAdding(false);
    },
  });

  if (!compact) {
    return (
      <div className="px-2 py-6 text-center">
        <p className="mb-2 text-sm text-neutral-400">Create a workspace to get started.</p>
        <InlineCreate placeholder="Workspace name" onCreate={(n) => add.mutate(n)} busy={add.isPending} />
      </div>
    );
  }
  return (
    <div className="mt-2 border-t border-neutral-100 px-2 pt-2">
      {adding ? (
        <InlineCreate
          placeholder="Workspace name"
          onCreate={(n) => add.mutate(n)}
          busy={add.isPending}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            if (canAdd) {
              setAdding(true);
            } else {
              showPlanLimit(
                'Multiple workspaces are a Business feature. Upgrade to Business to create additional workspaces.',
                () => navigate('/pricing'),
              );
            }
          }}
          className="w-full rounded-md px-2 py-1.5 text-left text-sm text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600"
        >
          + New workspace
        </button>
      )}
    </div>
  );
}

function InlineCreate({
  placeholder,
  onCreate,
  busy,
  onCancel,
  initial,
  saveOnBlur,
  maxLength = 120,
}: {
  placeholder: string;
  onCreate: (name: string) => void;
  busy?: boolean;
  onCancel?: () => void;
  initial?: string;
  saveOnBlur?: boolean;
  /** Client-side cap matching the server's name limit (120 for org names). */
  maxLength?: number;
}) {
  const [value, setValue] = useState(initial ?? '');
  const submitted = useRef(false);
  // Commit once. For rename (initial set) an unchanged value is a no-op.
  const commit = (): boolean => {
    const n = value.trim();
    if (!n || submitted.current || (initial !== undefined && n === initial)) return false;
    submitted.current = true;
    onCreate(n);
    if (initial === undefined) {
      setValue('');
      submitted.current = false;
    }
    return true;
  };
  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        commit();
      }}
      className="mt-1 flex items-center gap-1 px-1"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel?.();
        }}
        onBlur={() => {
          if (!value.trim()) onCancel?.();
          else if (saveOnBlur && !commit()) onCancel?.();
        }}
        placeholder={placeholder}
        disabled={busy}
        maxLength={maxLength}
        className="min-w-0 flex-1 rounded-md border border-neutral-300 px-2 py-1 text-sm text-neutral-700 outline-none placeholder:text-neutral-400 focus:border-neutral-900"
      />
      <button
        type="submit"
        title="Save"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
      >
        <IconCheck className="h-3.5 w-3.5" />
      </button>
      {onCancel && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onCancel}
          title="Cancel"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          ✕
        </button>
      )}
    </form>
  );
}
